import React from 'react';

import { Flex, RadioGroup } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { check1on1TranscriptionEnabled } from 'utils/helpers/featureFlags';

import { SettingsV1 } from 'assets/assetsV1/iconComponents';

import { sendClickedOnOneOnOneScoreSettings } from 'constants/mixpanelHelper';

import { Divider } from 'uiToolkitV1/Divider';
import { Radio } from 'uiToolkitV1/Radio';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { SettingsCardTemplate } from '../../components/common/SettingsCardTemplate';
import { OneOnOneSettingsPageReactQueryKeys } from '../../constants/OneOnOneSettingsPageReactQueryKeys';
import { useOneOnOneGeneralSettingsDetails } from '../../hooks/dataHooks';
import { setSaveButtonIsDisabled } from '../../slice/OneOnOneEventBasedAgendaSlice';

interface Props {}

const GeneralSettings = (props: Props) => {
  const { data, isLoading, isError } = useOneOnOneGeneralSettingsDetails();

  const queryClient = useQueryClient();

  const dispatch = useDispatch();

  const handleChange = (value: string) => {
    sendClickedOnOneOnOneScoreSettings({
      // Mixpanel Event
      eventProperties: {
        value,
      },
    });

    queryClient.setQueryData(
      [
        OneOnOneSettingsPageReactQueryKeys.oneOnOneScoreEnabled,
        OneOnOneSettingsPageReactQueryKeys.includeTranscriptionBot,
      ],
      (oldData: { isRatingEnabled: boolean }) => ({
        ...oldData,
        isRatingEnabled: value === 'true',
      })
    );
    dispatch(
      setSaveButtonIsDisabled({
        saveButtonIsDisabled: false,
      })
    );
  };

  const handleBotSettingsChange = (value: string) => {
    queryClient.setQueryData(
      [
        OneOnOneSettingsPageReactQueryKeys.oneOnOneScoreEnabled,
        OneOnOneSettingsPageReactQueryKeys.includeTranscriptionBot,
      ],
      (oldData: { includeTranscriptionBot: boolean }) => ({
        ...oldData,
        includeTranscriptionBot: value === 'true',
      })
    );
    dispatch(
      setSaveButtonIsDisabled({
        saveButtonIsDisabled: false,
      })
    );
  };

  const handleCarryForwardAgenda = (value: string) => {
    queryClient.setQueryData(
      [
        OneOnOneSettingsPageReactQueryKeys.oneOnOneScoreEnabled,
        OneOnOneSettingsPageReactQueryKeys.includeTranscriptionBot,
      ],
      (oldData: { isCarryForwardAgenda: boolean }) => ({
        ...oldData,
        isCarryForwardAgenda: value === 'true',
      })
    );
    dispatch(
      setSaveButtonIsDisabled({
        saveButtonIsDisabled: false,
      })
    );
  };

  return (
    <SettingsCardTemplate
      heading="1:1 Settings"
      subHeading="Set default settings for 1:1s between manager and their direct team member"
      Icon={SettingsV1}
      showDivider
    >
      <Flex flexDirection="column" ml="4px">
        <Flex ml="2px" flexDirection="column">
          <TextBodyMedium>Enable 1:1 satisfaction question </TextBodyMedium>
          <TextLabel color="#666666">
            Ask direct team member/skip team member this question for every 1:1 “On a scale of 1-5, how beneficial was
            your 1:1 meeting with your manager/skip-manager today?”
          </TextLabel>
        </Flex>
        <RadioGroup mt="8px" onChange={handleChange} value={data?.isRatingEnabled?.toString()}>
          {/* @ts-ignore */}
          <Radio size="sm" isChecked={data?.isRatingEnabled?.toString() === 'true'} value="true" mr="12px">
            Yes
          </Radio>
          {/* @ts-ignore */}
          <Radio size="sm" isChecked={data?.isRatingEnabled?.toString() === 'false'} value="false">
            No
          </Radio>
        </RadioGroup>
        {check1on1TranscriptionEnabled() && (
          <>
            <Divider my="12px" color="#EBEFF3" opacity={1} />
            <Flex flexDirection="column">
              <Flex ml="2px" flexDirection="column">
                <TextBodyMedium>
                  Enable Mesh bot to join 1:1 meetings to extract summary and meeting insights
                </TextBodyMedium>
                <TextLabel color="#666666">
                  Allows mesh bot to join every 1:1 meeting, transcribe it, summarise it and extract insights for
                  managers and admins
                </TextLabel>
              </Flex>
              <RadioGroup value={data?.includeTranscriptionBot?.toString()} mt="8px" onChange={handleBotSettingsChange}>
                {/* @ts-ignore */}
                <Radio
                  size="sm"
                  isChecked={data?.includeTranscriptionBot?.toString() === 'true'}
                  value="true"
                  mr="12px"
                >
                  Yes
                </Radio>
                {/* @ts-ignore */}
                <Radio size="sm" isChecked={data?.includeTranscriptionBot?.toString() === 'false'} value="false">
                  No
                </Radio>
              </RadioGroup>
            </Flex>
          </>
        )}
        <>
          <Divider my="12px" color="#EBEFF3" opacity={1} />
          <Flex flexDirection="column">
            <Flex ml="2px" flexDirection="column">
              <TextBodyMedium>Carrying over agenda points to the next 1:1 </TextBodyMedium>
              <TextLabel color="#666666">
                Agenda points that are not completed when a 1:1 is completed are carried over to the next 1:1 and if the
                1:1 is cancelled, all the agenda points are carried over to the next 1:1
              </TextLabel>
            </Flex>
            <RadioGroup value={data?.isCarryForwardAgenda?.toString()} mt="8px" onChange={handleCarryForwardAgenda}>
              {/* @ts-ignore */}
              <Radio size="sm" isChecked={data?.isCarryForwardAgenda?.toString() === 'true'} value="true" mr="12px">
                Yes
              </Radio>
              {/* @ts-ignore */}
              <Radio size="sm" isChecked={data?.isCarryForwardAgenda?.toString() === 'false'} value="false">
                No
              </Radio>
            </RadioGroup>
          </Flex>
        </>
      </Flex>
    </SettingsCardTemplate>
  );
};

export { GeneralSettings };
